import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, getDocs, collection } from 'firebase/firestore';
import { db, storage } from './firebase';
import { ref as storageRefVideo, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { C } from './constants';
import { useLang, useTranslatedProduit, DriveBtn } from './components';

// ── FORMATION PRODUITS ───────────────────────────────────────────────────────
// Espace dédié aux formations vidéo complètes (les vidéos se gèrent comme les autres contenus, depuis l'espace admin)
const CAT_VIDEO_ID = "formations_video";
const FORMATIONS_VIDEO_PAR_DEFAUT = [
  {id:"fv-formation-complete-1", titre:"Formation vidéo complète", description:"Formation vidéo complète. Regarde-la en entier, à ton rythme, et reviens dessus quand tu en as besoin.", videoUrl:"https://youtu.be/uUqL6QH6Znw", titreVideo:"Regarder la formation complète", image:"", supportUrl:"https://canva.link/vgualohb8bkiuis", dateAjout:"2026-10-07"},
];
// "Nouveau" : une formation vidéo est considérée comme nouvelle pendant 3 semaines après son ajout
const JOURS_NOUVEAU = 21;
const CLE_VIDEOS_VUES = "bd-fp-videos-vues";
const estRecente = (dateAjout) => { if(!dateAjout) return false; const t=new Date(dateAjout+"T00:00:00").getTime(); return !isNaN(t) && (Date.now()-t) < JOURS_NOUVEAU*86400000; };
const dateAjoutDe = (p) => (p && p.dateAjout) || ((FORMATIONS_VIDEO_PAR_DEFAUT.find(d=>p&&d.id===p.id)||{}).dateAjout) || "";
const lireVideosVues = () => { try{ return JSON.parse(localStorage.getItem(CLE_VIDEOS_VUES)||"[]"); }catch(e){ return []; } };
const marquerVideosVues = (ids) => { try{ const deja=new Set(lireVideosVues()); ids.forEach(i=>deja.add(i)); localStorage.setItem(CLE_VIDEOS_VUES,JSON.stringify([...deja])); }catch(e){} };
function idYouTube(url){
  const m=String(url||"").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/))([A-Za-z0-9_-]{11})/);
  return m?m[1]:null;
}

const CATEGORIES_PRODUITS = [
  {id:CAT_VIDEO_ID,   label:"Formations vidéo",         icon:"🎬", color:"#C44B1A"},
  {id:"parfum",        label:"Parfum",                  icon:"🌸", color:"#9B59B6"},
  {id:"makeup",        label:"Maquillage",               icon:"💄", color:"#E91E8C"},
  {id:"complement",    label:"Compléments alimentaires", icon:"💊", color:"#27AE60"},
  {id:"nutrition",     label:"Nutrition",                icon:"🥗", color:"#7CB342"},
  {id:"poids",         label:"Perte de poids",           icon:"⚖️", color:"#E67E22"},
  {id:"skincare",      label:"Skincare",                 icon:"✨", color:"#C49A8A"},
  {id:"cheveux",       label:"Soins cheveux",            icon:"💇", color:"#8E44AD"},
  {id:"corpsoin",      label:"Soins corps",              icon:"🧴", color:"#3498DB"},
  {id:"entretien",     label:"Produits d'entretien",     icon:"🏠", color:"#7F8C8D"},
  {id:"problematiques",label:"Par Problématiques",       icon:"🎯", color:"#C0392B"},
];

// Sous-thèmes de la catégorie Problématiques
const PROBLEMATIQUES_THEMES = [
  {id:"anti_age",       label:"Anti-âge",            icon:"⏳", color:"#8E44AD"},
  {id:"acne",           label:"Acné / Imperfections", icon:"🌿", color:"#27AE60"},
  {id:"peau_seche",     label:"Peau sèche",           icon:"💧", color:"#3498DB"},
  {id:"peau_grasse",    label:"Peau grasse",          icon:"🫧", color:"#1ABC9C"},
  {id:"eclat",          label:"Éclat / Teint terne",  icon:"☀️", color:"#F39C12"},
  {id:"cernes",         label:"Cernes / Fatigue",     icon:"🌙", color:"#6C3483"},
  {id:"cellulite",      label:"Cellulite / Fermeté",  icon:"💪", color:"#E67E22"},
  {id:"cheveux_abimes", label:"Cheveux abîmés",        icon:"💇", color:"#5D6D7E"},
  {id:"perte_cheveux",  label:"Perte de cheveux",     icon:"🔄", color:"#C0392B"},
  {id:"stress",         label:"Stress / Sommeil",     icon:"😴", color:"#2980B9"},
  {id:"digestion",      label:"Digestion",            icon:"🌱", color:"#27AE60"},
  {id:"immunite",       label:"Immunité",             icon:"🛡️", color:"#E74C3C"},
  {id:"energie",        label:"Énergie / Vitalité",   icon:"⚡", color:"#F1C40F"},
  {id:"minceur",        label:"Minceur globale",      icon:"🎯", color:"#E67E22"},
];


// Charge toute la formation produits : { produits: {categorie: [...]}, derniereMaj: ms }
// Lit la structure par categorie, avec repli sur l'ancien document unique si besoin.
async function chargerFormationProduits(){
  try{
    const snap=await getDocs(collection(db,"admin","formation_produits_categories","items"));
    if(!snap.empty){
      const produits={}; let maj=0;
      snap.forEach(d=>{ produits[d.id]=d.data().items||[]; maj=Math.max(maj,d.data().derniereMaj||0); });
      if(produits[CAT_VIDEO_ID]===undefined) maj=Math.max(maj,Date.parse(FORMATIONS_VIDEO_PAR_DEFAUT[0].dateAjout+"T12:00:00Z")||0);
      return {produits,derniereMaj:maj};
    }
  }catch(e){ console.error("chargerFormationProduits (categories):",e); }
  try{
    const ancien=await getDoc(doc(db,"admin","formation_produits"));
    if(ancien.exists()) return {produits:ancien.data().produits||{},derniereMaj:ancien.data().derniereMaj||0};
  }catch(e){ console.error("chargerFormationProduits (ancien):",e); }
  return {produits:{},derniereMaj:0};
}

function FormationProduitsTab(){
  const {lang} = useLang();
  const[produits,setProduits]=useState({});
  const[loading,setLoading]=useState(true);
  const[catActive,setCatActive]=useState("parfum");
  const[produitOuvert,setProduitOuvert]=useState(null); // {id, cat}
  const[ongletProduit,setOngletProduit]=useState("texte");
  const[makeupSousOnglet,setMakeupSousOnglet]=useState("produits"); // produits | tips
  const[vuesInit]=useState(()=>lireVideosVues()); // formations vidéo déjà vues avant cette visite
  const[videoOuverteId,setVideoOuverteId]=useState(null);
  const autoOuvertFait=React.useRef(false);

  useEffect(()=>{
    (async()=>{
      try{
        const snapNouvelleStructure=await getDocs(collection(db,"admin","formation_produits_categories","items"));
        if(!snapNouvelleStructure.empty){
          const produitsCharges={};
          snapNouvelleStructure.forEach(d=>{produitsCharges[d.id]=d.data().items||[];});
          setProduits(produitsCharges);
        }else{
          const snap=await getDoc(doc(db,"admin","formation_produits"));
          if(snap.exists()) setProduits(snap.data().produits||{});
        }
      }catch{}
      setLoading(false);
    })();
  },[]);

  const listeVideos=produits[CAT_VIDEO_ID]===undefined?FORMATIONS_VIDEO_PAR_DEFAUT:(produits[CAT_VIDEO_ID]||[]);
  const videosTriees=[...listeVideos].sort((a,b)=>String(dateAjoutDe(b)).localeCompare(String(dateAjoutDe(a))));
  const videosNouvellesNonVues=listeVideos.filter(v=>estRecente(dateAjoutDe(v))&&!vuesInit.includes(v.id));
  // "NEW" : seulement pour une formation récente que la personne n'a pas encore vue (masqué dès qu'elle quitte l'espace vidéo)
  const[masquees,setMasquees]=useState([]);
  const estNouvelle=(v)=>estRecente(dateAjoutDe(v))&&!vuesInit.includes(v.id)&&!masquees.includes(v.id);
  const videosAffichablesNew=listeVideos.some(estNouvelle);
  // Première visite avec une nouvelle formation vidéo : on ouvre directement l'espace vidéo
  useEffect(()=>{
    if(loading||autoOuvertFait.current)return;
    autoOuvertFait.current=true;
    if(videosNouvellesNonVues.length>0)setCatActive(CAT_VIDEO_ID);
  // eslint-disable-next-line
  },[loading]);
  // Dès que l'espace vidéo est affiché, les formations sont marquées comme vues
  useEffect(()=>{
    if(!loading&&catActive===CAT_VIDEO_ID){
      marquerVideosVues(listeVideos.map(v=>v.id));
      return()=>setMasquees(listeVideos.map(v=>v.id));
    }
  // eslint-disable-next-line
  },[loading,catActive]);

  const cat=CATEGORIES_PRODUITS.find(c=>c.id===catActive)||CATEGORIES_PRODUITS[0];
  const[themeActif,setThemeActif]=useState(null);
  const makeupKey = catActive==="makeup" ? (makeupSousOnglet==="tips" ? "makeup_tips" : "makeup") : catActive;
  const listeCat=(makeupKey===CAT_VIDEO_ID&&produits[CAT_VIDEO_ID]===undefined)?FORMATIONS_VIDEO_PAR_DEFAUT:(produits[makeupKey]||[]);
  // Pour problématiques : filtrer par thème si sélectionné
  const listeAffichee=catActive==="problematiques"&&themeActif
    ?listeCat.filter(p=>p.theme===themeActif)
    :listeCat;
  const produitActifRaw=produitOuvert?listeCat.find(p=>p.id===produitOuvert.id):null;
  const produitActif=useTranslatedProduit(produitActifRaw);

  if(loading)return <div style={{textAlign:"center",padding:"2rem",color:C.gris}}>Chargement...</div>;

  // Vue détail produit
  if(produitActif){
    return(
      <div>
        {/* Header produit */}
        <button onClick={()=>{setProduitOuvert(null);setOngletProduit("texte");}}
          style={{background:"none",border:"none",color:C.gris,fontSize:".75rem",cursor:"pointer",fontFamily:"inherit",marginBottom:"1rem",padding:0,display:"flex",alignItems:"center",gap:".3rem"}}>
          ← Retour {cat.label}
        </button>

        {/* Image avec titre superposé */}
        <div style={{position:"relative",borderRadius:14,overflow:"hidden",marginBottom:"1rem",minHeight:produitActif.image?200:80}}>
          {produitActif.image
            ?<img src={produitActif.image} alt={produitActif.titre} style={{width:"100%",maxHeight:240,objectFit:"cover",display:"block"}}/>
            :<div style={{background:`linear-gradient(135deg,${cat.color},${cat.color}88)`,height:120}}/>
          }
          <div style={{position:"absolute",top:0,left:0,right:0,padding:"1rem",background:"linear-gradient(180deg,rgba(0,0,0,.55) 0%,transparent 100%)"}}>
            <div style={{fontSize:".55rem",fontWeight:700,color:"rgba(255,255,255,.8)",letterSpacing:".12em",textTransform:"uppercase",marginBottom:".2rem"}}>{cat.icon} {cat.label}</div>
            <div style={{fontFamily:"Georgia,serif",fontSize:"1.1rem",fontWeight:600,color:"white",lineHeight:1.35}}>{produitActif.titre}</div>
          </div>
        </div>

        {/* Onglets Texte / Vidéo */}
        <div style={{display:"flex",gap:".4rem",marginBottom:"1rem"}}>
          {[{id:"texte",label:"📄 Texte"},{id:"video",label:"▶ Vidéo"}].map(o=>(
            <button key={o.id} onClick={()=>setOngletProduit(o.id)}
              style={{flex:1,padding:".5rem",fontSize:".78rem",fontWeight:600,borderRadius:10,border:`1.5px solid ${ongletProduit===o.id?cat.color:C.pale}`,background:ongletProduit===o.id?cat.color:C.blanc,color:ongletProduit===o.id?"white":C.gris,cursor:"pointer",fontFamily:"inherit"}}>
              {o.label}
            </button>
          ))}
        </div>

        {/* Contenu */}
        {ongletProduit==="texte"&&(
          <div style={{background:C.blanc,border:`1px solid ${C.pale}`,borderRadius:12,padding:"1rem"}}>
            {produitActif.description
              ?<div style={{fontSize:".82rem",color:C.texte,lineHeight:1.75,whiteSpace:"pre-wrap"}}>{produitActif.description}</div>
              :<div style={{textAlign:"center",padding:"1.5rem",color:C.gris,fontSize:".75rem",fontStyle:"italic"}}>Aucun texte pour ce produit.</div>
            }
          </div>
        )}

        {ongletProduit==="video"&&(
          <div style={{background:C.blanc,border:`1px solid ${C.pale}`,borderRadius:12,padding:"1rem"}}>
            {produitActif.videoUrl
              ?<a href={produitActif.videoUrl} target="_blank" rel="noopener noreferrer"
                  style={{display:"flex",alignItems:"center",gap:".75rem",background:`linear-gradient(135deg,${cat.color},${cat.color}aa)`,borderRadius:10,padding:".85rem 1rem",textDecoration:"none"}}>
                  <div style={{width:44,height:44,borderRadius:"50%",background:"rgba(255,255,255,.25)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.3rem",flexShrink:0}}>▶</div>
                  <div>
                    <div style={{fontFamily:"Georgia,serif",fontSize:".9rem",fontWeight:600,color:"white"}}>{produitActif.titreVideo||"Voir la vidéo"}</div>
                    <div style={{fontSize:".65rem",color:"rgba(255,255,255,.75)",marginTop:".15rem"}}>Cliquer pour regarder</div>
                  </div>
                </a>
              :<div style={{textAlign:"center",padding:"1.5rem",color:C.gris,fontSize:".75rem",fontStyle:"italic"}}>
                🎬 Vidéo à venir prochainement
              </div>
            }
          </div>
        )}
      </div>
    );
  }

  // Vue liste
  return(
    <div>
      <div style={{fontFamily:"Georgia,serif",fontSize:"1.35rem",fontWeight:300,color:C.brun,marginBottom:".75rem"}}>
        Formation <em style={{fontStyle:"italic",color:C.rose}}>Produits</em>
      </div>

      {/* Catégories */}
      <div style={{display:"flex",gap:".3rem",overflowX:"auto",paddingBottom:".3rem",marginBottom:"1rem"}}>
        {CATEGORIES_PRODUITS.map(c=>(
          <button key={c.id} onClick={()=>{setCatActive(c.id);setProduitOuvert(null);}}
            style={{flexShrink:0,padding:".42rem .75rem",fontSize:".7rem",fontWeight:600,borderRadius:20,border:`1.5px solid ${catActive===c.id?c.color:C.pale}`,background:catActive===c.id?c.color:C.blanc,color:catActive===c.id?"white":C.gris,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
            {c.icon} {c.label}
            {c.id===CAT_VIDEO_ID&&videosAffichablesNew&&<span style={{marginLeft:".35rem",background:"#E53935",color:"white",fontSize:".5rem",fontWeight:800,borderRadius:8,padding:".08rem .32rem",letterSpacing:".04em",verticalAlign:"middle"}}>NEW</span>}
          </button>
        ))}
      </div>

      {cat.id===CAT_VIDEO_ID&&(
        <div style={{background:`${cat.color}12`,border:`1px solid ${cat.color}40`,borderRadius:12,padding:".75rem .9rem",marginBottom:"1rem",fontSize:".76rem",color:C.brun,lineHeight:1.6}}>
          🎬 <b>Formations vidéo complètes</b> : les formations à regarder en entier, à ton rythme. De nouvelles vidéos s'ajoutent ici au fil du temps 💛
        </div>
      )}

      {/* Sous-onglets Maquillage */}
      {catActive==="makeup"&&(
        <div style={{display:"flex",gap:".4rem",marginBottom:"1rem"}}>
          {[{id:"produits",label:"💄 Produits"},{id:"tips",label:"✨ Tips & Astuces"}].map(o=>(
            <button key={o.id} onClick={()=>setMakeupSousOnglet(o.id)}
              style={{flex:1,padding:".5rem",fontSize:".78rem",fontWeight:600,borderRadius:10,border:"1.5px solid "+(makeupSousOnglet===o.id?"#E91E8C":C.pale),background:makeupSousOnglet===o.id?"#E91E8C":"white",color:makeupSousOnglet===o.id?"white":C.gris,cursor:"pointer",fontFamily:"inherit"}}>
              {o.label}
            </button>
          ))}
        </div>
      )}
      {catActive==="makeup"&&(
        <div style={{background:C.blanc,border:`1px solid ${C.pale}`,borderRadius:12,padding:".85rem 1rem",marginBottom:"1rem"}}>
          <div style={{fontSize:".6rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"#E91E8C",marginBottom:".5rem"}}>🎥 Formations vidéo Maquillage</div>
          <DriveBtn href="https://drive.google.com/file/d/18pMW1mXZLdMqHdPTF1_n4ezefCZf6eom/view" label="Formation Make-up — Mise en beauté"/>
          <DriveBtn href="https://drive.google.com/file/d/1R73lC1mlEAgssAzLX-oX58-rG_gwvPms/view" label="Formation Make-up — Glow eyes"/>
        </div>
      )}

      {/* Sous-thèmes pour Problématiques */}
      {catActive==="problematiques"&&(
        <div style={{marginBottom:".75rem"}}>
          <div style={{fontSize:".6rem",color:C.gris,fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",marginBottom:".4rem"}}>Filtrer par problématique</div>
          <div style={{display:"flex",gap:".25rem",flexWrap:"wrap"}}>
            <button onClick={()=>setThemeActif(null)}
              style={{padding:".28rem .6rem",fontSize:".65rem",fontWeight:600,borderRadius:20,border:`1.5px solid ${!themeActif?"#C0392B":C.pale}`,background:!themeActif?"#C0392B":C.blanc,color:!themeActif?"white":C.gris,cursor:"pointer",fontFamily:"inherit"}}>
              Tout voir ({listeCat.length})
            </button>
            {PROBLEMATIQUES_THEMES.filter(t=>listeCat.some(p=>p.theme===t.id)).map(t=>(
              <button key={t.id} onClick={()=>setThemeActif(themeActif===t.id?null:t.id)}
                style={{padding:".28rem .6rem",fontSize:".65rem",fontWeight:600,borderRadius:20,border:`1.5px solid ${themeActif===t.id?t.color:C.pale}`,background:themeActif===t.id?t.color:C.blanc,color:themeActif===t.id?"white":C.gris,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                {t.icon} {t.label} ({listeCat.filter(p=>p.theme===t.id).length})
              </button>
            ))}
            {/* Afficher les thèmes vides en gris si tous les thèmes */}
            {PROBLEMATIQUES_THEMES.filter(t=>!listeCat.some(p=>p.theme===t.id)).map(t=>(
              <span key={t.id} style={{padding:".28rem .6rem",fontSize:".62rem",borderRadius:20,border:`1px solid ${C.pale}`,color:C.pale,whiteSpace:"nowrap"}}>
                {t.icon} {t.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Grille produits */}
      {listeAffichee.length===0
        ?<div style={{textAlign:"center",padding:"2rem",color:C.gris,fontSize:".76rem",fontStyle:"italic"}}>
            {catActive===CAT_VIDEO_ID?"Aucune formation vidéo pour l'instant.":catActive==="problematiques"&&themeActif?"Aucun produit pour cette problématique.":"Aucun produit dans cette catégorie pour l'instant."}
          </div>
        :cat.id===CAT_VIDEO_ID
        ?<div style={{display:"flex",flexDirection:"column",gap:"1rem"}}>
          {videosTriees.map(p=>{
            const idYT=idYouTube(p.videoUrl);
            const ouverte=(videoOuverteId||videosTriees[0]?.id)===p.id;
            const vignette=p.image||(idYT?`https://img.youtube.com/vi/${idYT}/hqdefault.jpg`:"");
            return(
              <div key={p.id} style={{background:C.blanc,border:`1.5px solid ${estNouvelle(p)?cat.color:C.pale}`,borderRadius:14,overflow:"hidden",boxShadow:"0 2px 10px rgba(0,0,0,.06)"}}>
                {ouverte&&idYT
                  ?<div style={{position:"relative",paddingBottom:"56.25%",height:0,overflow:"hidden",background:"#000"}}>
                      <iframe src={`https://www.youtube.com/embed/${idYT}`} title={p.titre}
                        style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",border:"none"}}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen/>
                    </div>
                  :<div onClick={()=>setVideoOuverteId(p.id)} style={{position:"relative",paddingBottom:"56.25%",background:`linear-gradient(135deg,${cat.color},${cat.color}88)`,cursor:"pointer"}}>
                      {vignette&&<img src={vignette} alt={p.titre} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"cover"}}/>}
                      <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <div style={{width:56,height:56,borderRadius:"50%",background:"rgba(0,0,0,.55)",color:"white",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.4rem"}}>▶</div>
                      </div>
                    </div>
                }
                <div style={{padding:".85rem .95rem"}}>
                  {estNouvelle(p)&&<span style={{display:"inline-block",background:"#E53935",color:"white",fontSize:".58rem",fontWeight:800,borderRadius:8,padding:".12rem .45rem",letterSpacing:".05em",marginBottom:".4rem"}}>🆕 NOUVEAU</span>}
                  <div style={{fontFamily:"Georgia,serif",fontSize:"1rem",fontWeight:600,color:C.brun,lineHeight:1.35}}>{p.titre}</div>
                  {p.description&&<div style={{fontSize:".76rem",color:C.gris,lineHeight:1.6,marginTop:".35rem",whiteSpace:"pre-wrap"}}>{ouverte?p.description:(p.description.slice(0,110)+(p.description.length>110?"…":""))}</div>}
                  {p.supportUrl&&(
                    <a href={p.supportUrl} target="_blank" rel="noopener noreferrer"
                      style={{display:"flex",alignItems:"center",gap:".6rem",background:`${cat.color}12`,border:`1.5px solid ${cat.color}`,borderRadius:12,padding:".7rem .95rem",textDecoration:"none",marginTop:".75rem"}}>
                      <span style={{fontSize:"1.15rem"}}>📎</span>
                      <span style={{fontSize:".82rem",fontWeight:700,color:cat.color}}>Voir le support de formation</span>
                    </a>
                  )}
                  {!idYT&&p.videoUrl&&(
                    <a href={p.videoUrl} target="_blank" rel="noopener noreferrer"
                      style={{display:"inline-block",marginTop:".6rem",fontSize:".78rem",fontWeight:700,color:cat.color}}>▶ {p.titreVideo||"Voir la vidéo"}</a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        :<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".65rem"}}>
          {listeAffichee.map(p=>(
            <div key={p.id} onClick={()=>{setProduitOuvert({id:p.id,cat:catActive});setOngletProduit("texte");}}
              style={{background:C.blanc,border:`1px solid ${C.pale}`,borderRadius:12,overflow:"hidden",cursor:"pointer",boxShadow:"0 2px 8px rgba(0,0,0,.05)",transition:"transform .15s"}}
              onTouchStart={e=>e.currentTarget.style.transform="scale(.97)"}
              onTouchEnd={e=>e.currentTarget.style.transform="scale(1)"}>
              {/* Image */}
              <div style={{position:"relative",height:110,background:`linear-gradient(135deg,${cat.color}30,${cat.color}15)`,overflow:"hidden"}}>
                {p.image
                  ?<img src={p.image} alt={p.titre} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  :<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",fontSize:"2.5rem"}}>{cat.icon}</div>
                }
                {/* Titre superposé en haut à gauche */}
                <div style={{position:"absolute",top:0,left:0,right:0,padding:".5rem .6rem",background:"linear-gradient(180deg,rgba(0,0,0,.6) 0%,transparent 100%)"}}>
                  <div style={{fontFamily:"Georgia,serif",fontSize:".78rem",fontWeight:700,color:"white",lineHeight:1.3,textShadow:"0 1px 3px rgba(0,0,0,.5)"}}>{p.titre}</div>
                </div>
                {/* Badge vidéo */}
                {p.videoUrl&&<div style={{position:"absolute",bottom:4,right:4,background:"rgba(0,0,0,.5)",borderRadius:20,padding:".1rem .35rem",fontSize:".55rem",color:"white"}}>▶ Vidéo</div>}
              </div>
              {/* Extrait */}
              {p.description&&<div style={{padding:".5rem .65rem",fontSize:".68rem",color:C.gris,lineHeight:1.5}}>
                {p.description.slice(0,80)}{p.description.length>80?"...":""}
              </div>}
            </div>
          ))}
        </div>
      }
    </div>
  );
}

// Admin Formation Produits
// Composant upload photo — sélection depuis galerie OU URL
export function UploadPhoto({value, onChange, label="Photo", folder="produits", maxSize=400, quality=0.7}){
  const[uploading,setUploading]=useState(false);
  const[preview,setPreview]=useState(value||"");

  useEffect(()=>setPreview(value||""),[value]);

  const handleFile=async(e)=>{
    const file=e.target.files?.[0];
    if(!file)return;
    setUploading(true);
    try{
      // Preview immédiat
      const reader=new FileReader();
      reader.onload=ev=>setPreview(ev.target.result);
      reader.readAsDataURL(file);
      // Compresser l image avant sauvegarde
      const img=new Image();
      img.onload=()=>{
        const canvas=document.createElement("canvas");
        const MAX=maxSize;
        let w=img.width,h=img.height;
        if(w>h){if(w>MAX){h=h*(MAX/w);w=MAX;}}else{if(h>MAX){w=w*(MAX/h);h=MAX;}}
        canvas.width=w;canvas.height=h;
        canvas.getContext("2d").drawImage(img,0,0,w,h);
        const compressed=canvas.toDataURL("image/jpeg",quality);
        onChange(compressed);
      };
      const reader2=new FileReader();reader2.onload=ev=>{img.src=ev.target.result;setUploading(false);};reader2.readAsDataURL(file);return;
    }catch(err){
      alert("Erreur upload : "+err.message);
    }
    setUploading(false);
  };

  return(
    <div style={{marginBottom:".55rem"}}>
      <div style={{fontSize:".6rem",color:C.gris,marginBottom:".2rem",fontWeight:600,textTransform:"uppercase",letterSpacing:".08em"}}>{label}</div>
      <div style={{display:"flex",gap:".4rem",alignItems:"flex-start"}}>
        {preview&&<img src={preview} alt="" style={{width:52,height:52,borderRadius:8,objectFit:"cover",flexShrink:0,border:`1px solid ${C.pale}`}}/>}
        <div style={{flex:1}}>
          <label style={{display:"block",background:uploading?"#aaa":C.brun,color:"white",borderRadius:8,padding:".38rem .65rem",fontSize:".72rem",fontWeight:600,textAlign:"center",cursor:uploading?"default":"pointer",fontFamily:"inherit",marginBottom:".25rem"}}>
            {uploading?"⏳ Envoi en cours...":preview?"🔄 Changer la photo":"📷 Choisir une photo"}
            <input type="file" accept="image/*" onChange={handleFile} style={{display:"none"}} disabled={uploading} key={preview}/>
          </label>
          {preview&&<button onClick={()=>{setPreview("");onChange("");}} style={{display:"block",width:"100%",background:"none",border:`1px solid ${C.pale}`,borderRadius:7,padding:".22rem .5rem",fontSize:".65rem",color:C.gris,cursor:"pointer",fontFamily:"inherit",marginBottom:".25rem"}}>✕ Supprimer</button>}
          <input value={value||""} onChange={e=>{onChange(e.target.value);setPreview(e.target.value);}} placeholder="...ou coller une URL"
            style={{width:"100%",border:`1px solid ${C.pale}`,borderRadius:7,padding:".32rem .5rem",fontSize:".7rem",fontFamily:"inherit",color:C.texte,background:C.creme,outline:"none"}}/>
        </div>
      </div>
    </div>
  );
}
export function UploadVideo({value, onChange, label="Video", folder="linkbio-videos", uid="anon"}){
  const[uploading,setUploading]=useState(false);
  const[progress,setProgress]=useState(0);
  const[preview,setPreview]=useState(value||"");
  useEffect(()=>setPreview(value||""),[value]);
  const handleFile=async(e)=>{
    const file=e.target.files?.[0];
    if(!file)return;
    if(file.size>50*1024*1024){ alert("Video trop volumineuse (50 Mo max)."); return; }
    setUploading(true);setProgress(0);
    try{
      const path=`${folder}/${uid}/${Date.now()}-${file.name}`;
      const fileRef=storageRefVideo(storage,path);
      const uploadTask=uploadBytesResumable(fileRef,file);
      uploadTask.on("state_changed",
        (snap)=>{ setProgress(Math.round((snap.bytesTransferred/snap.totalBytes)*100)); },
        (err)=>{ alert("Erreur upload video : "+err.message); setUploading(false); },
        async()=>{
          const url=await getDownloadURL(uploadTask.snapshot.ref);
          setPreview(url);
          onChange(url);
          setUploading(false);
        }
      );
    }catch(err){
      alert("Erreur upload : "+err.message);
      setUploading(false);
    }
  };
  return(
    <div style={{marginBottom:".55rem"}}>
      <div style={{fontSize:".6rem",color:C.gris,marginBottom:".2rem",fontWeight:600,textTransform:"uppercase",letterSpacing:".08em"}}>{label}</div>
      {preview&&<video src={preview} controls style={{width:"100%",maxWidth:220,borderRadius:8,marginBottom:".3rem",display:"block"}}/>}
      <label style={{display:"block",background:uploading?"#aaa":C.brun,color:"white",borderRadius:8,padding:".38rem .65rem",fontSize:".72rem",fontWeight:600,textAlign:"center",cursor:uploading?"default":"pointer",fontFamily:"inherit",marginBottom:".25rem"}}>
        {uploading?`Envoi... ${progress}%`:preview?"Changer la video":"Choisir une video"}
        <input type="file" accept="video/*" onChange={handleFile} style={{display:"none"}} disabled={uploading}/>
      </label>
      {preview&&!uploading&&<button onClick={()=>{setPreview("");onChange("");}} style={{display:"block",width:"100%",background:"none",border:`1px solid ${C.pale}`,borderRadius:7,padding:".22rem .5rem",fontSize:".65rem",color:C.gris,cursor:"pointer",fontFamily:"inherit"}}>Supprimer la video</button>}
    </div>
  );
}

// Composant global — ne jamais définir à l'intérieur d'un autre composant
function FormField({label, value, onChange, placeholder, textarea=false, type="text"}){
  return(
    <div style={{marginBottom:".5rem"}}>
      <div style={{fontSize:".6rem",color:C.gris,marginBottom:".2rem",fontWeight:600,textTransform:"uppercase",letterSpacing:".08em"}}>{label}</div>
      {textarea
        ?<textarea value={value||""} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={5}
            style={{width:"100%",border:`1px solid ${C.pale}`,borderRadius:8,padding:".42rem .65rem",fontSize:".8rem",fontFamily:"inherit",color:C.texte,background:C.creme,outline:"none",resize:"vertical",lineHeight:1.6}}/>
        :<input type={type} value={value||""} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
            style={{width:"100%",border:`1px solid ${C.pale}`,borderRadius:8,padding:".42rem .65rem",fontSize:".8rem",fontFamily:"inherit",color:C.texte,background:C.creme,outline:"none"}}/>
      }
    </div>
  );
}

function AdminFormationProduits({uid}={}){
  const[produits,setProduits]=useState({});
  const[loading,setLoading]=useState(true);
  const[catActive,setCatActive]=useState("parfum");
  const[showForm,setShowForm]=useState(false);
  const[editId,setEditId]=useState(null);
  const[form,setForm]=useState({titre:"",image:"",description:"",videoUrl:"",titreVideo:"",theme:"",refCatalogue:"",supportUrl:""});
  const[saving,setSaving]=useState(false);
  const[catalogueProduits,setCatalogueProduits]=useState(null);
  const[filtreCatalogue,setFiltreCatalogue]=useState("");
  const[liageOuvertPour,setLiageOuvertPour]=useState(null);
  const[filtreLiageRapide,setFiltreLiageRapide]=useState("");

  const[liageEnCours,setLiageEnCours]=useState(false);
  const lierRapide=async(prodFormation,refCatalogue)=>{
    if(liageEnCours)return;
    setLiageEnCours(true);
    const next={...produits};
    const cat=Object.keys(next).find(c=>(next[c]||[]).some(p=>p.id===prodFormation.id));
    if(cat){
      next[cat]=next[cat].map(p=>{
        if(p.id!==prodFormation.id)return p;
        const actuels=p.refsCatalogue||(p.refCatalogue?[p.refCatalogue]:[]);
        const dejaLie=actuels.includes(refCatalogue);
        const nouveaux=dejaLie?actuels.filter(r=>r!==refCatalogue):[...actuels,refCatalogue];
        const {refCatalogue:_omis, ...pSansAncienChamp}=p;
        return {...pSansAncienChamp,refsCatalogue:nouveaux};
      });
      await save(next);
    }
    setLiageEnCours(false);
  };

  useEffect(()=>{
    (async()=>{
      try{
        // Nouvelle structure : un document Firestore par categorie (evite la limite de 1 Mo par document)
        const snapNouvelleStructure=await getDocs(collection(db,"admin","formation_produits_categories","items"));
        if(!snapNouvelleStructure.empty){
          const produitsCharges={};
          snapNouvelleStructure.forEach(d=>{produitsCharges[d.id]=d.data().items||[];});
          setProduits(produitsCharges);
        }else{
          // Migration automatique unique depuis l'ancien document unique (trop volumineux desormais)
          const snapAncien=await getDoc(doc(db,"admin","formation_produits"));
          const produitsAnciens=snapAncien.exists()?(snapAncien.data().produits||{}):{};
          setProduits(produitsAnciens);
          for(const[cat,items] of Object.entries(produitsAnciens)){
            try{await setDoc(doc(db,"admin","formation_produits_categories","items",cat),{items,derniereMaj:Date.now()});}catch(e){console.error("Migration categorie "+cat+" echouee:",e);}
          }
        }
      }catch{}
      try{
        // Espace formations vidéo : enregistre la première formation pour qu'elle devienne modifiable/supprimable
        const refVideo=doc(db,"admin","formation_produits_categories","items",CAT_VIDEO_ID);
        const verif=await getDoc(refVideo);
        if(!verif.exists()){
          await setDoc(refVideo,{items:FORMATIONS_VIDEO_PAR_DEFAUT,derniereMaj:Date.now()});
          setProduits(p=>({...p,[CAT_VIDEO_ID]:FORMATIONS_VIDEO_PAR_DEFAUT}));
        }else{
          // Répare les formations enregistrées avant l'ajout du champ "date d'ajout"
          const itemsActuels=verif.data().items||[];
          if(itemsActuels.some(it=>!it.dateAjout&&dateAjoutDe(it))){
            const repares=itemsActuels.map(it=>(!it.dateAjout&&dateAjoutDe(it))?{...it,dateAjout:dateAjoutDe(it)}:it);
            await setDoc(refVideo,{items:repares,derniereMaj:verif.data().derniereMaj||Date.now()});
            setProduits(p=>({...p,[CAT_VIDEO_ID]:repares}));
          }
        }
      }catch(e){console.error("Creation espace formations video:",e);}
      try{
        const catSnap=await getDoc(doc(db,"admin","catalogue_mihi"));
        if(catSnap.exists()) setCatalogueProduits(Object.values(catSnap.data()).flat());
      }catch{}
      setLoading(false);
    })();
  },[]);

  const save=async(nextProduits)=>{
    setSaving(true);
    try{
      // N'ecrit que les categories qui ont reellement change, chacune dans son propre document
      const categoriesModifiees=new Set([...Object.keys(nextProduits),...Object.keys(produits)].filter(cat=>JSON.stringify(nextProduits[cat]||[])!==JSON.stringify(produits[cat]||[])));
      for(const cat of categoriesModifiees){
        await setDoc(doc(db,"admin","formation_produits_categories","items",cat),{items:nextProduits[cat]||[],derniereMaj:Date.now()});
      }
      setProduits(nextProduits);
    }catch(e){console.error("Erreur sauvegarde formation produits:",e);alert("Erreur lors de l'enregistrement : "+e.message);}
    setSaving(false);
  };

  const ajouter=async()=>{
    if(!form.titre.trim())return;
    const cat=catActive;
    const listeCat=produits[cat]||[];
    let next;
    if(editId){
      next={...produits,[cat]:listeCat.map(p=>p.id===editId?{...p,...form}:p)};
    } else {
      next={...produits,[cat]:[...listeCat,{id:`p${Date.now()}`,...form,...(cat===CAT_VIDEO_ID?{dateAjout:new Date().toISOString().slice(0,10)}:{})}]};
    }
    await save(next);
    setForm({titre:"",image:"",description:"",videoUrl:"",titreVideo:"",theme:"",refCatalogue:"",supportUrl:""});
    setShowForm(false);setEditId(null);setFiltreCatalogue("");
  };

  const supprimer=async(cat,id)=>{
    if(!window.confirm("Supprimer ce produit ?"))return;
    await save({...produits,[cat]:(produits[cat]||[]).filter(p=>p.id!==id)});
  };

  // État réel de la notification pour mon compte (pour comprendre pourquoi la pastille apparaît ou non)
  const[diagNotif,setDiagNotif]=useState(null);
  const chargerDiagNotif=async()=>{
    if(!uid)return;
    try{
      const u=await getDoc(doc(db,"users",uid));
      const brut=u.exists()?u.data()["db-formation-progress"]:null;
      const prog=brut?JSON.parse(brut):{};
      const vue=(prog.formaproduits||{}).derniereConsultation||null;
      const fp=await chargerFormationProduits();
      const maj=fp.derniereMaj?new Date(fp.derniereMaj).toISOString():null;
      const liste=fp.produits[CAT_VIDEO_ID]===undefined?FORMATIONS_VIDEO_PAR_DEFAUT:(fp.produits[CAT_VIDEO_ID]||[]);
      const recentes=liste.filter(v=>estRecente(dateAjoutDe(v)));
      const vues=lireVideosVues();
      setDiagNotif({vue,maj,pastille:!!maj&&(!vue||new Date(vue)<new Date(maj)),nbVideos:liste.length,nbRecentes:recentes.length,nbNonVues:recentes.filter(v=>!vues.includes(v.id)).length});
    }catch(e){setDiagNotif({erreur:e.message});}
  };
  useEffect(()=>{chargerDiagNotif();},[uid]);

  // Aperçu de la notification "NOUVEAU" comme la voit l'équipe (efface mon suivi de consultation, puis recharge l'app)
  const revoirNotification=async()=>{
    if(!uid)return;
    try{
      const ref=doc(db,"users",uid);
      const snap=await getDoc(ref);
      const brut=snap.exists()?snap.data()["db-formation-progress"]:null;
      const progress=brut?JSON.parse(brut):{};
      if(progress.formaproduits)delete progress.formaproduits.derniereConsultation;
      await setDoc(ref,{"db-formation-progress":JSON.stringify(progress)},{merge:true});
      try{localStorage.removeItem(CLE_VIDEOS_VUES);}catch(e){}
      await chargerDiagNotif();
      alert("C'est fait ✅\n\nRecharge l'app (Ctrl+Shift+R) : tu verras la pastille rouge sur « Formation » et le tag NOUVEAU sur « Formation Produits ». En ouvrant Formation, tu arriveras directement sur les formations vidéo, comme l'équipe.\n\nElle disparaîtra de nouveau dès que tu seras passée par là.");
    }catch(e){ alert("Erreur : "+e.message); }
  };

  if(loading)return null;

  const listeCat=produits[catActive]||[];
  const estVideo=catActive===CAT_VIDEO_ID;

  return(
    <div style={{background:C.blanc,border:`1px solid ${C.pale}`,borderRadius:12,padding:"1rem",marginBottom:"1.25rem"}}>
      <div style={{fontSize:".6rem",fontWeight:700,color:C.rose,letterSpacing:".1em",textTransform:"uppercase",marginBottom:".75rem"}}>🧴 Formation Produits — Gestion du contenu</div>

      {uid&&(
        <div style={{background:C.creme,border:`1px dashed ${C.pale}`,borderRadius:10,padding:".6rem .8rem",marginBottom:".75rem"}}>
          <div style={{fontSize:".68rem",color:C.gris,lineHeight:1.5,marginBottom:".45rem"}}>La notification « NOUVEAU » s'efface chez toi dès que tu ouvres Formation Produits. Ce bouton te la fait revoir comme l'équipe.</div>
          <button onClick={revoirNotification}
            style={{width:"100%",background:C.blanc,color:C.brun,border:`1.5px solid ${C.rose}`,borderRadius:8,padding:".42rem",fontSize:".72rem",fontWeight:700,fontFamily:"inherit",cursor:"pointer"}}>
            👁 Voir la notification comme l'équipe
          </button>
          {diagNotif&&(
            <div style={{marginTop:".5rem",fontSize:".64rem",color:C.gris,lineHeight:1.6}}>
              {diagNotif.erreur?<>Lecture impossible : {diagNotif.erreur}</>:<>
                <div>🔴 Pastille dans le menu : <b style={{color:diagNotif.pastille?"#E53935":C.brun}}>{diagNotif.pastille?"OUI":"NON"}</b> <span>(ta dernière visite : {diagNotif.vue?new Date(diagNotif.vue).toLocaleString("fr-FR"):"jamais"} · dernière mise à jour : {diagNotif.maj?new Date(diagNotif.maj).toLocaleString("fr-FR"):"inconnue"})</span></div>
                <div>🎬 Formations vidéo : {diagNotif.nbVideos} · récentes : {diagNotif.nbRecentes} · pas encore vues dans ce navigateur : {diagNotif.nbNonVues} → ouverture automatique : <b style={{color:diagNotif.nbNonVues>0?"#E53935":C.brun}}>{diagNotif.nbNonVues>0?"OUI":"NON"}</b></div>
              </>}
            </div>
          )}
        </div>
      )}

      {/* Catégories */}
      <div style={{display:"flex",gap:".25rem",overflowX:"auto",marginBottom:".75rem",paddingBottom:".2rem"}}>
        {CATEGORIES_PRODUITS.map(c=>(
          <button key={c.id} onClick={()=>{setCatActive(c.id);setShowForm(false);setEditId(null);}}
            style={{flexShrink:0,padding:".3rem .55rem",fontSize:".65rem",fontWeight:600,borderRadius:8,border:`1.5px solid ${catActive===c.id?c.color:C.pale}`,background:catActive===c.id?c.color:C.blanc,color:catActive===c.id?"white":C.gris,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
            {c.icon} {c.label}
          </button>
        ))}
      </div>

      {/* Formulaire */}
      {showForm&&(
        <div id="formation-produits-formulaire" style={{background:C.creme,borderRadius:10,padding:".85rem",marginBottom:".75rem",border:`1px solid ${C.pale}`}}>
          <div style={{fontSize:".65rem",fontWeight:700,color:C.brun,marginBottom:".6rem"}}>{editId?"✏️ Modifier":(estVideo?"+ Nouvelle formation vidéo":"+ Nouveau produit")} — {CATEGORIES_PRODUITS.find(c=>c.id===catActive)?.label}</div>
          <FormField label={estVideo?"Titre de la formation *":"Titre du produit *"} value={form.titre} onChange={v=>setForm(p=>({...p,titre:v}))} placeholder="Ex: Eau de parfum Rose Dorée"/>
          {!estVideo&&<div style={{fontSize:".68rem",color:C.gris,background:C.blanc,border:`1px solid ${C.pale}`,borderRadius:8,padding:".5rem .7rem",marginBottom:".5rem",lineHeight:1.5}}>
            💡 Pour lier ce contenu à un ou plusieurs produits de la boutique, ferme ce formulaire et utilise le bouton "🔗 Gérer les produits liés" directement sur la fiche dans la liste.
          </div>}
          <UploadPhoto label={estVideo?"Image de la formation (facultatif : sinon la miniature YouTube est utilisée)":"Image du produit"} value={form.image} onChange={v=>setForm(p=>({...p,image:v}))} folder="produits"/>
          {catActive==="problematiques"&&(
            <div style={{marginBottom:".5rem"}}>
              <div style={{fontSize:".6rem",color:C.gris,marginBottom:".3rem",fontWeight:600,textTransform:"uppercase",letterSpacing:".08em"}}>🎯 Problématique</div>
              <select value={form.theme} onChange={e=>setForm(p=>({...p,theme:e.target.value}))}
                style={{width:"100%",border:`1px solid ${C.pale}`,borderRadius:8,padding:".42rem .65rem",fontSize:".8rem",fontFamily:"inherit",color:C.texte,background:C.creme,outline:"none"}}>
                <option value="">— Choisir une problématique —</option>
                {PROBLEMATIQUES_THEMES.map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
              </select>
            </div>
          )}
          <FormField label="Texte / Description" value={form.description} onChange={v=>setForm(p=>({...p,description:v}))} placeholder="Composition, bienfaits, conseils d'utilisation..." textarea={true}/>
          <FormField label="Lien vidéo (YouTube, Zoom...)" value={form.videoUrl} onChange={v=>setForm(p=>({...p,videoUrl:v}))} placeholder="https://..."/>
          <FormField label="Titre de la vidéo" value={form.titreVideo} onChange={v=>setForm(p=>({...p,titreVideo:v}))} placeholder="Ex: Présentation de la gamme"/>
          {estVideo&&<FormField label="Support de formation (lien Canva, PDF… — facultatif)" value={form.supportUrl} onChange={v=>setForm(p=>({...p,supportUrl:v}))} placeholder="https://..."/>}
          <div style={{display:"flex",gap:".4rem",marginTop:".25rem"}}>
            <button onClick={ajouter} disabled={saving||!form.titre.trim()}
              style={{flex:1,background:C.brun,color:C.blanc,border:"none",borderRadius:8,padding:".48rem",fontSize:".78rem",fontWeight:600,fontFamily:"inherit",cursor:"pointer"}}>
              {saving?"Sauvegarde...":editId?"✓ Modifier":"✓ Ajouter"}
            </button>
            <button onClick={()=>{setShowForm(false);setEditId(null);setForm({titre:"",image:"",description:"",videoUrl:"",titreVideo:"",theme:"",refCatalogue:"",supportUrl:""});setFiltreCatalogue(""); }}
              style={{flex:1,background:C.pale,color:C.gris,border:"none",borderRadius:8,padding:".48rem",fontSize:".78rem",fontFamily:"inherit",cursor:"pointer"}}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {!showForm&&<button onClick={()=>setShowForm(true)}
        style={{width:"100%",background:C.brun,color:C.blanc,border:"none",borderRadius:8,padding:".48rem",fontSize:".78rem",fontWeight:600,fontFamily:"inherit",cursor:"pointer",marginBottom:".6rem"}}>
        {estVideo?"+ Ajouter une formation vidéo":"+ Ajouter un produit dans "+CATEGORIES_PRODUITS.find(c=>c.id===catActive)?.label}
      </button>}

      {/* Liste existante */}
      {listeCat.map(p=>(
        <div key={p.id} style={{marginBottom:".35rem"}}>
          <div style={{display:"flex",alignItems:"center",gap:".5rem",background:C.creme,borderRadius:9,padding:".5rem .75rem",border:`1px solid ${C.pale}`}}>
            {p.image&&<img src={p.image} alt="" style={{width:40,height:40,borderRadius:7,objectFit:"cover",flexShrink:0}}/>}
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:".78rem",fontWeight:600,color:C.brun,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.titre}</div>
              <div style={{fontSize:".6rem",color:C.gris}}>{p.videoUrl?"▶ Vidéo · ":""}{p.theme?`🎯 ${PROBLEMATIQUES_THEMES.find(t=>t.id===p.theme)?.label||p.theme} · `:""}{p.description?.slice(0,40)||"Pas de texte"}{p.description?.length>40?"...":""}</div>
              {!estVideo&&<>{(()=>{
                const refsLies=p.refsCatalogue||(p.refCatalogue?[p.refCatalogue]:[]);
                return refsLies.length>0&&catalogueProduits?(
                  <div style={{fontSize:".6rem",color:"#2D5A3D",fontWeight:600,marginTop:".2rem"}}>
                    ✅ Lié à {refsLies.length} produit{refsLies.length>1?"s":""} : {refsLies.map(r=>catalogueProduits.find(cp=>cp.ref===r)?.nom||r).join(", ")}
                  </div>
                ):null;
              })()}
              <button onClick={()=>{setLiageOuvertPour(liageOuvertPour===p.id?null:p.id);setFiltreLiageRapide("");}}
                style={{marginTop:".2rem",background:"none",border:"none",padding:0,fontSize:".62rem",color:"#C44B1A",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                {(p.refsCatalogue||(p.refCatalogue?[p.refCatalogue]:[])).length>0?"🔗 Gérer les produits liés":"⚠️ Pas encore lié à la boutique — cliquer pour lier"}
              </button></>}
            </div>
            <button onClick={()=>{setEditId(p.id);setForm({titre:p.titre||"",image:p.image||"",description:p.description||"",videoUrl:p.videoUrl||"",titreVideo:p.titreVideo||"",theme:p.theme||"",refCatalogue:p.refCatalogue||"",supportUrl:p.supportUrl||""});setShowForm(true);setTimeout(()=>{document.getElementById("formation-produits-formulaire")?.scrollIntoView({behavior:"smooth",block:"start"});},100);}}
              style={{background:"none",border:`1px solid ${C.pale}`,borderRadius:6,padding:".2rem .4rem",fontSize:".62rem",color:C.gris,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>✏️</button>
            <button onClick={()=>supprimer(catActive,p.id)}
              style={{background:"none",border:`1px solid ${C.pale}`,borderRadius:6,padding:".2rem .4rem",fontSize:".62rem",color:"#B04040",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>✕</button>
          </div>
          {liageOuvertPour===p.id&&(
            <div style={{background:"white",border:`1.5px solid #C44B1A`,borderRadius:9,padding:".6rem",marginTop:".3rem"}}>
              {!catalogueProduits?(
                <div style={{fontSize:".7rem",color:C.gris}}>Chargement du catalogue...</div>
              ):(<>
                <div style={{fontSize:".62rem",color:C.gris,marginBottom:".4rem",fontStyle:"italic"}}>Coche tous les produits qui utilisent cette même formation (ex : les différents coloris d'un fond de teint).</div>
                <input value={filtreLiageRapide} onChange={e=>setFiltreLiageRapide(e.target.value)} placeholder="🔍 Rechercher un produit..." autoFocus
                  style={{width:"100%",border:`1px solid ${C.pale}`,borderRadius:8,padding:".4rem .6rem",fontSize:".74rem",fontFamily:"inherit",outline:"none",marginBottom:".4rem"}}/>
                {filtreLiageRapide.trim()&&(
                  <div style={{maxHeight:200,overflowY:"auto",border:`1px solid ${C.pale}`,borderRadius:8,marginBottom:".5rem",opacity:liageEnCours?.6:1,pointerEvents:liageEnCours?"none":"auto"}}>
                    {catalogueProduits.filter(cp=>cp.nom.toLowerCase().includes(filtreLiageRapide.toLowerCase())).slice(0,30).map(cp=>{
                      const refsLies=p.refsCatalogue||(p.refCatalogue?[p.refCatalogue]:[]);
                      const coche=refsLies.includes(cp.ref);
                      return(
                        <div key={cp.ref} onClick={()=>lierRapide(p,cp.ref)}
                          style={{display:"flex",alignItems:"center",gap:".5rem",padding:".4rem .6rem",fontSize:".72rem",cursor:"pointer",borderBottom:`1px solid ${C.pale}`,background:coche?"#E8F5E9":"transparent"}}>
                          <div style={{width:16,height:16,borderRadius:4,border:`1.5px solid ${coche?"#2D5A3D":C.pale}`,background:coche?"#2D5A3D":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            {coche&&<span style={{color:"white",fontSize:".6rem",fontWeight:700}}>✓</span>}
                          </div>
                          <span style={{flex:1}}>{cp.nom}</span>
                          <span style={{color:C.gris}}>{cp.prix?.toFixed?.(2)}€</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <button onClick={()=>{setLiageOuvertPour(null);setFiltreLiageRapide("");}}
                  style={{width:"100%",background:C.brun,color:"white",border:"none",borderRadius:8,padding:".4rem",fontSize:".72rem",fontWeight:600,fontFamily:"inherit",cursor:"pointer"}}>
                  Terminé
                </button>
              </>)}
            </div>
          )}
        </div>
      ))}
      {listeCat.length===0&&<div style={{textAlign:"center",fontSize:".72rem",color:C.gris,padding:".5rem",fontStyle:"italic"}}>{estVideo?"Aucune formation vidéo — ajoute la première !":"Aucun produit — ajoute le premier !"}</div>}
    </div>
  );
}



export { FormationProduitsTab, AdminFormationProduits, CATEGORIES_PRODUITS, chargerFormationProduits, CAT_VIDEO_ID };
